<script>
import {ref} from "vue";
import useTools from "@/use/Tools";
import useValidation from "@/use/Validation";
import axios from "axios";
import {useSecurityStore} from "@/stores/Security";
import useSharedSelectData from "@/use/SharedSelectData";
import EvaluationFileListingDataTable from "@/views/Evaluation/File/Components/EvaluationFileListingDataTable.vue";
import EvaluationFileDataTable from "@/views/Evaluation/File/Components/EvaluationFileDataTable.vue";
import useSharedData from "@/use/SharedData";
import {useEvaluationFileStore} from "@/stores/Evaluation/EvaluationFile";

export default {
    components: {EvaluationFileDataTable, EvaluationFileListingDataTable},
    setup() {
        const {mxRules} = useValidation();
        const {$swal, $refs, $moment} = useTools();

        const {loading, backgroundColor} = useSharedData();
        const {
            evaluationFileTypes,
            evaluationFileScopes,
            evaluationCycles,
            months,
            quarterlies,
            halfYearlies
        } = useSharedSelectData();

        const evaluationFileStore = useEvaluationFileStore();
        const securityStore = useSecurityStore();

        const dialog = ref(false);
        const file = ref(null);

        const type = ref('EVALUATION_PERFORMANCE');
        const name = ref(null);
        const scope = ref('WORKER');
        const cycle = ref(null);
        const modelId = ref(null);
        const cycleMonth = ref(null);
        const cycleQuarterlyOrHalfYearly = ref(null);
        const cycleYear = ref($moment().year());
        const individualCategoryId = ref(securityStore.userContext.defaultIndividualEvaluationPerformanceCategory);
        const individualCategoryGravity = ref(null);
        const commonCategoryId = ref(securityStore.userContext.defaultCommonEvaluationPerformanceCategory);
        const commonCategoryGravity = ref(null);
        const commonCriteria = ref([{
            name: null,
            gravity: 100
        }]);

        const evaluationFiles = ref([]);
        const evaluationListingFiles = ref([]);

        const logs = ref([]);
        const logDialog = ref(false);

        function upload() {
            if ($refs.form.validate()) {
                loading.value = true;

                commonCriteria.value.forEach((criterion, index) => {
                    criterion.order = index;
                });

                let indexDto = {
                    name: name.value,
                    modelId: modelId.value,
                    type: type.value,
                    scope: 'EVALUATION_PERFORMANCE' === type.value ? scope.value : 'OCCUPATION',
                    cycleMonth: cycleMonth.value,
                    cycleQuarterlyOrHalfYearly: cycleQuarterlyOrHalfYearly.value,
                    cycleYear: cycleYear.value,
                    categories: [
                        {
                            categoryId: individualCategoryId.value,
                            gravity: individualCategoryGravity.value,
                            distribution: 'INDIVIDUAL',
                            order: 0,
                            criteria: [],
                        },
                        {
                            categoryId: commonCategoryId.value,
                            gravity: commonCategoryGravity.value,
                            distribution: 'COMMON',
                            order: 1,
                            criteria: commonCriteria.value,
                        }
                    ],
                    occupationIds: [],
                    workerIds: [],
                };

                let formData = new FormData();
                formData.append('file', file.value);
                formData.append('index', new Blob([JSON.stringify(indexDto)], {type: 'application/json'}));

                axios.post(`/tools/getEvaluationFiles`, formData).then(response => {
                    evaluationFiles.value = response.data.fileElements;
                    evaluationListingFiles.value = response.data.listingElements;
                    logs.value = response.data.logs;
                    dialog.value = false;

                    /* if (logs.value.length > 0)
                        logDialog.value = true;*/
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function apply() {
            if (evaluationFiles.value.length > 0) {
                loading.value = true;
                axios.post(`/evaluationFiles/saveAll`, evaluationFiles.value, {
                    params: {
                        modelId: modelId.value,
                    }
                }).then(() => {
                    $swal.fire('Très Bien', "Importation des objectifs terminé", 'success')
                    evaluationFiles.value = [];
                    evaluationListingFiles.value = [];
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function findModelById() {
            if (modelId.value) {
                axios.get(`/evaluationFileModels/${modelId.value}`).then(response => {
                    cycle.value = response.data.cycle;
                });
            }
        }

        function updateCriterion({original, item}) {
            const file = evaluationFiles.value.find(f => f.code === original.fileCode);
            if (!file) return false;

            for (const category of file.categories ?? []) {
                const criterion = (category.criteria ?? []).find(
                    c => c.measureName === original.measureName
                );

                if (criterion) {
                    criterion.measureName = item.measureName;
                    criterion.measureKeyPerformanceIndicator = item.measureKeyPerformanceIndicator;
                    criterion.measureNotes = item.measureNotes;
                    criterion.gravity = item.gravity;
                    criterion.target = item.target;
                    criterion.deadline = item.deadline;
                    return true;
                }
            }

            return false;
        }

        function addCriterion(item) {
            const file = evaluationFiles.value.find(f => f.code === item.fileCode);
            const category = file?.categories?.[0];            // first category by default
            if (!category || !item.measureName) return false;

            const criteria = category.criteria ?? [];

            // next order: highest existing + 1 (0 when the category is empty)
            const order = criteria.reduce((max, c) => Math.max(max, c.order ?? -1), -1) + 1;

            // 1. data that gets saved: a real criterion, shaped like its siblings
            const template = criteria[criteria.length - 1] ?? {};
            category.criteria = [
                ...criteria,
                {
                    ...template,
                    id: null,
                    measureName: item.measureName,
                    measureKeyPerformanceIndicator: item.measureKeyPerformanceIndicator,
                    measureNotes: item.measureNotes,
                    gravity: item.gravity,
                    target: item.target,
                    deadline: item.deadline,
                    order
                },
            ];

            // 2. the table: insert a real row after the last row of this worker
            const list = evaluationListingFiles.value;
            let last = -1;
            list.forEach((r, i) => {
                if (r.code === item.code) last = i;
            });
            list.splice(last === -1 ? list.length : last + 1, 0, {...item, isNew: false});

            return true;
        }

        function view(item) {
            evaluationFileStore.setEvaluationFile(item);
            // $router.push({name: 'evaluation_files_tools.view', params: {type: type.value}});
        }

        return {
            securityStore,
            mxRules,
            dialog,
            loading,
            backgroundColor,
            file,
            type,
            name,
            scope,
            cycle,
            modelId,
            cycleMonth,
            cycleQuarterlyOrHalfYearly,
            cycleYear,
            individualCategoryId,
            individualCategoryGravity,
            commonCategoryId,
            commonCategoryGravity,
            commonCriteria,
            evaluationFileTypes,
            evaluationFileScopes,
            evaluationFiles,
            evaluationListingFiles,
            evaluationCycles,
            months,
            quarterlies,
            halfYearlies,
            logs,
            logDialog,
            apply,
            upload,
            findModelById,
            addCriterion,
            updateCriterion,
            view,
        }
    }
}
</script>

<template>
    <div>
        <w-app
            :title="$t('module.evaluation_files_tools')"
            show-total
            :total="evaluationFiles.length"
        >
            <template v-slot:leftToolbar>
                <!--<ImportModel file-name="Fonction.xlsx"/>-->
            </template>
            <template v-slot:rightToolbar>
                <w-btn
                    color="teal"
                    icon="mdi-import"
                    :text="$t('common.import')"
                    @press="dialog = true"
                />
                <!--<w-btn
                    color="orange"
                    icon="mdi-skull-scan"
                    :text="$t('common.logs')"
                    @press="logDialog = true"
                />-->
                <w-btn
                    v-if="securityStore.authorities['UPDATE_IMPORT_PARAM']"
                    color="red"
                    icon="mdi-table-check"
                    :text="$t('common.apply')"
                    :loading="loading"
                    @press="apply"
                />

            </template>

            <template v-slot:content>
                <v-tabs
                    class="tabs"
                    color="teal"
                    :background-color="backgroundColor"
                    slider-size="5"
                >
                    <v-tab
                        v-for="(category, index) in evaluationListingFiles"
                        :key="`tab-${index}-${category.name}`"
                    >
                        {{ $t(`${category.name} (${category.gravity}%)`) }}
                    </v-tab>
                    <v-tab>
                        {{ $t(`evaluation.file_display_mode_FILE`) }}
                    </v-tab>

                    <v-tab-item
                        v-for="(category, index) in evaluationListingFiles"
                        :key="`tab-item-${index}-${category.name}`"
                    >
                        <evaluation-file-listing-data-table
                            identity="code"
                            :items="category.criteria"
                            :loading="loading"
                            readonly
                            :common="category.distribution === 'COMMON'"
                            @add="addCriterion"
                            @update="updateCriterion"
                            type="EVALUATION_PERFORMANCE"
                        />

                    </v-tab-item>
                    <v-tab-item>
                        <evaluation-file-data-table
                            identity="code"
                            :items="evaluationFiles"
                            :loading="loading"
                            :root-path="'rootPath'"
                            intranet
                            @view="view"
                        />
                    </v-tab-item>
                </v-tabs>
            </template>
        </w-app>
        <v-dialog
            v-model="dialog"
            persistent
            max-width="700"
            scrollable
        >
            <v-card>
                <w-toolbar color="teal" :title="$t('common.import')"/>
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="form" lazy-validation>
                            <w-fieldset>
                                <v-row dense>
                                    <v-col cols="12">
                                        <w-label :label="$t('common.label')" required/>
                                        <w-text-field
                                            v-model="name"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <!--<v-col cols="12">
                                        <w-label :label="$t('common.type')" required/>
                                        <w-enum-select
                                            v-model="type"
                                            :items="evaluationFileTypes"
                                            not-clearable
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>-->
                                    <!--<v-col cols="6">
                                        <w-label :label="$t('common.type')" required/>
                                        <w-enum-select
                                            v-model="scope"
                                            :items="evaluationFileScopes"
                                            not-clearable
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>-->
                                    <v-col cols="12">
                                        <w-label :label="$t('common.model')"/>
                                        <str-evaluation-file-model-autocomplete
                                            v-model="modelId"
                                            :rules="[mxRules.required]"
                                            @change="findModelById"
                                        />
                                    </v-col>
                                    <v-col cols="6" v-if="cycle === 'MONTHLY'">
                                        <w-label :label="$t('common.month')" required/>
                                        <w-enum-select
                                            v-model="cycleMonth"
                                            :items="months"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6" v-if="cycle === 'QUARTERLY'">
                                        <w-label :label="$t('common.QUARTERLY')" required/>
                                        <w-enum-select
                                            v-model="cycleQuarterlyOrHalfYearly"
                                            :items="quarterlies"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6" v-if="cycle === 'HALF_YEARLY'">
                                        <w-label :label="$t('common.HALF_YEARLY')" required/>
                                        <w-enum-select
                                            v-model="cycleQuarterlyOrHalfYearly"
                                            :items="halfYearlies"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col
                                        v-if="cycle"
                                        :cols="cycle === 'YEARLY' ? '12' : '6'"
                                    >
                                        <w-label :label="$t('common.year')" required/>
                                        <w-text-field
                                            v-model="cycleYear"
                                            type="number"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset>
                                <v-row dense>
                                    <v-col cols="8">
                                        <w-label label="Section" required/>
                                        <w-data-select-autocomplete
                                            v-model="individualCategoryId"
                                            type="EVALUATION_PERFORMANCE_CRITERION_CATEGORY"
                                            readonly
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="4">
                                        <w-label label="Pondération" required/>
                                        <w-text-field
                                            v-model="individualCategoryGravity"
                                            type="number"
                                            :rules="[mxRules.required, mxRules.maxValue(100), mxRules.minValue(0)]"
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <w-label :label="$t('common.file')" required/>
                                        <w-file-input
                                            v-model="file"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset  v-if="securityStore.userContext.activateCommonEvaluationPerformanceCategory">
                                <v-row>
                                    <v-col cols="8">
                                        <w-label label="Section" required/>
                                        <w-data-select-autocomplete
                                            v-model="commonCategoryId"
                                            type="EVALUATION_PERFORMANCE_CRITERION_CATEGORY"
                                            readonly
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="4">
                                        <w-label label="Pondération" required/>
                                        <w-text-field
                                            v-model="commonCategoryGravity"
                                            type="number"
                                            :rules="[mxRules.required, mxRules.maxValue(100), mxRules.minValue(0)]"
                                        />
                                    </v-col>
                                </v-row>
                                <v-divider class="mt-6 mb-6"/>
                                <v-simple-table dense>
                                    <template #default>
                                        <thead>
                                        <tr>
                                            <th>Objectif</th>
                                            <th>KPI</th>
                                            <th>Pondération</th>
                                            <th>Deadline</th>
                                            <th></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr
                                            v-for="(criterion, index) in commonCriteria"
                                            :key="`criterion-${index}`"
                                        >
                                            <td>
                                                <w-edit-dialog
                                                    v-model="criterion.name"
                                                    disable-inline
                                                    hide-chip
                                                />
                                            </td>
                                            <td>
                                                <w-edit-dialog
                                                    v-model="criterion.keyPerformanceIndicator"
                                                    disable-inline
                                                    hide-chip
                                                />
                                            </td>
                                            <td>
                                                <w-edit-dialog
                                                    v-model="criterion.gravity"
                                                    type="number"
                                                    :rules="[mxRules.required, mxRules.maxValue(100), mxRules.minValue(0)]"
                                                />
                                            </td>
                                            <td>
                                                <w-edit-dialog
                                                    v-model="criterion.deadline"
                                                    date-format
                                                    large
                                                    :rules="[mxRules.required]"
                                                />
                                            </td>
                                            <td>
                                                <w-btn-icon
                                                    icon="mdi-trash-can"
                                                    color="red"
                                                    :disabled="commonCriteria.length === 1"
                                                    @press="commonCriteria.splice(index, 1)"
                                                />
                                            </td>
                                        </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </w-fieldset>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="dialog = false"/>
                    <w-btn-import
                        :loading="loading"
                        @press="upload"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="logDialog"
            max-width="600"
            scrollable
        >
            <v-card>
                <w-toolbar color="orange" title="Logs"/>
                <v-card-text>
                    <v-container fluid>
                        <v-list dense>
                            <v-list-item
                                v-for="(log, index) in logs"
                                :key="`log-${index}`"
                            >
                                <v-list-item-title>{{ log }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-dialog>

        <router-view/>
    </div>
</template>

<style scoped>

</style>